import * as THREE from 'three';
import AbstractObject from '@scene/misc/object';
import NailGroup from './nail-group';
import defaultOptions from '@object-generators/wood-connection/defaultOptions';
import { ScrewGroup } from '../screw';
import { addDistanceLabel, mergeDeep, unitTransform } from '@services/helper.service';
import { defaultUnits } from '@config';
import Scene from '@scene';
import TextLabel from '../text';
import Arrow from '../arrow';
import ObjectView from '@scene/views/object-view';

export default class NailPlate extends AbstractObject {

  static DIMENSIONS_OFFSET = 15; // offset from the edge of the plate

  constructor(options = {}) {
    super(options);
    this._nailGroup = null;
    this._screwGroup = null;
    this._labels = {
      plateDimensions: {
        ALabel: {},
        BLabel: {},
        CLabel: {},
        DLabel: {}
      },
      cLabels: [],
      sLabels: []
    };

    this.options = {
      ...options,
      position: options.position,
      rotation: options.rotation
    };
    this.init();
  }

  get dimensions() {
    return this.options.panel.dimensions;
  }
  get verticalPartThickness() {
    return this.options.panel.thickness.tfixA;
  }
  get horizontalPartThickness() {
    return this.options.panel.thickness.tfixB;
  }
  get screwGroup() {
    return this._screwGroup;
  }

  init() {
    const verticalPart = new THREE.Mesh(
      new THREE.BoxGeometry(this.verticalPartThickness, this.dimensions.a, this.dimensions.c),
      new THREE.MeshStandardMaterial({
        color: defaultOptions.materials.metalColor,
        roughness: defaultOptions.materials.properties.metal.roughness,
        metalness: defaultOptions.materials.properties.metal.metalness
      })
    );

    const verticalPartGeometry = verticalPart.geometry;
    const verticalPartSize = new THREE.Vector3();
    verticalPartGeometry.computeBoundingBox();
    verticalPartGeometry.boundingBox.getSize(verticalPartSize);

    const horizontalPart = new THREE.Mesh(
      new THREE.BoxGeometry(this.dimensions.b, this.horizontalPartThickness, this.dimensions.d),
      new THREE.MeshStandardMaterial({
        color: defaultOptions.materials.metalColor,
        roughness: defaultOptions.materials.properties.metal.roughness,
        metalness: defaultOptions.materials.properties.metal.metalness
      })
    );

    const horizontalPartGeometry = horizontalPart.geometry;
    const horizontalPartSize = new THREE.Vector3();
    horizontalPartGeometry.computeBoundingBox();
    horizontalPartGeometry.boundingBox.getSize(horizontalPartSize);

    verticalPart.position.y = this.dimensions.a / 2 + this.horizontalPartThickness / 2;
    horizontalPart.position.x = this.dimensions.b / 2 - this.verticalPartThickness / 2;

    this.mesh.add(verticalPart);
    this.mesh.add(horizontalPart);

    this.mesh.position.copy(this.options.position);
    this.mesh.rotation.copy(this.options.rotation);

    if (this.options.object.dimensions.display)
      this._labels.plateDimensions = this.createPlateDimensions();

    const nailGroup = new NailGroup({
      ...this.options,
      holeDepth: this.verticalPartThickness,
      plateHeight: this.dimensions.a,
      plateWidth: this.dimensions.c
    });

    this._nailGroup = nailGroup;
    this._nailGroup.onOptionUpdate(this.dispatchOptionUpdate.bind(this));

    nailGroup.mesh.position.set(
      verticalPartSize.x / 2,
      this.dimensions.a + this.horizontalPartThickness / 2,
      0
    );

    nailGroup.mesh.rotation.set(-Math.PI / 2, 0, Math.PI / 2);

    if(this.options.object.dimensions.display)
      this._labels = mergeDeep(this._labels, nailGroup.createColumnDimensions());
    this.mesh.add(nailGroup.mesh);
    this.htmlLayer.addLayer(nailGroup.htmlLayer);

    this._screwGroup = new ScrewGroup({
      ...this.options.screw,
      custom: {
        ...this.options.screw.custom,
        positions: this.options.screw.custom.positions.map(a => new THREE.Vector3(a.z, -a.x, -a.y))
      },
      panel: {
        depth: this.horizontalPartThickness
      }
    });
    this._screwGroup.onOptionUpdate(this.dispatchOptionUpdate.bind(this));
    
    this._screwGroup.mesh.position.set(
      this.dimensions.b / 2,
      horizontalPartSize.y / 2,
      0
    );

    this._screwGroup.mesh.rotation.set(0, Math.PI / 2, Math.PI / 2);
    this.object.addObject(this._screwGroup);
  }

  setPosition(position) {
    this.mesh.position.copy(position);
  }

  setRotation(rotation) {
    this.mesh.rotation.copy(rotation);
  }

  get mesh() {
    return this.object.mesh;
  }

  get labels() {
    return this._labels;
  }

  get showLabels() {
    return this.options.mode === ObjectView.mode.MODE_2D || this.options.object.dimensions.showLabels;
  }

  createPlateDimensions() {
    const plateDimensions = {};

    const ALabel = this.createPlateDimensionA();
    plateDimensions.ALabel = ALabel;
    this.mesh.add(ALabel.object);
    this.htmlLayer.add(ALabel.text);

    if (this.options.mode === ObjectView.mode.MODE_3D) {
      const BLabel = this.create3DPlateDimensionB();
      plateDimensions.BLabel = BLabel;
      this.mesh.add(BLabel.object);
      this.htmlLayer.add(BLabel.text);
    }
    
    const CLabel = this.createPlateDimensionC();
    plateDimensions.CLabel = CLabel;
    this.mesh.add(CLabel.object);
    this.htmlLayer.add(CLabel.text);

    const DLabel = this.options.mode === ObjectView.mode.MODE_3D ? this.create3DPlateDimensionD() : this.create2DPlateDimensionD();
    plateDimensions.DLabel = DLabel;
    this.mesh.add(DLabel.object);
    this.htmlLayer.add(DLabel.text);

    return plateDimensions;
  }

  createPlateDimensionA() {
    let top = {
      from: {
        x: 0,
        y: this.horizontalPartThickness / 2,
        z: -this.dimensions.c / 2
      },
      to: {
        x: 0,
        y: this.horizontalPartThickness / 2,
        z: -this.dimensions.c / 2 - NailPlate.DIMENSIONS_OFFSET,
      },
    };
    let bottom = {
      from: {
        x: 0,
        y: this.dimensions.a + this.horizontalPartThickness / 2,
        z: -this.dimensions.c / 2
      },
      to: {
        x: 0,
        y: this.dimensions.a + this.horizontalPartThickness / 2,
        z: -this.dimensions.c / 2 - NailPlate.DIMENSIONS_OFFSET
      },
    };

    const AValue = unitTransform(this.dimensions.a).from(defaultUnits.DISTANCE).to(Scene.units.distance);
    const ALabel = addDistanceLabel({
      top,
      bottom,
      size: this.dimensions.a,
      arrowDirection: Arrow.types.direction.Y,
      arrowSize: 5,
      labelText: (!this.options || this.showLabels ? 'A = ' : '') + `${AValue}`,
      labelValue: AValue,
      align: TextLabel.types.align.LEFT,
      verticalAlign: TextLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          panel: {
            dimensions: {
              a: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }, false, didChange);
      }
    });
    ALabel.text.position.set(
      NailPlate.DIMENSIONS_OFFSET + this.dimensions.c / 2,
      0,
      - this.dimensions.a / 2
    );
    
    return {
      object: ALabel.object,
      text: ALabel.text
    };
  }

  create3DPlateDimensionB() {
    let top = {
      from: {
        x: - this.verticalPartThickness / 2,
        y: 0,
        z: -this.dimensions.d / 2
      },
      to: {
        x: - this.verticalPartThickness / 2,
        y: 0,
        z: -this.dimensions.d / 2 - NailPlate.DIMENSIONS_OFFSET,
      },
    };
    let bottom = {
      from: {
        x: this.dimensions.b - this.verticalPartThickness / 2,
        y: 0,
        z: -this.dimensions.d / 2
      },
      to: {
        x: this.dimensions.b - this.verticalPartThickness / 2,
        y: 0,
        z: -this.dimensions.d / 2 - NailPlate.DIMENSIONS_OFFSET
      },
    };

    const BValue = unitTransform(this.dimensions.b).from(defaultUnits.DISTANCE).to(Scene.units.distance);
    const BLabel = addDistanceLabel({
      top,
      bottom,
      size: this.dimensions.b,
      arrowDirection: Arrow.types.direction.X,
      arrowSize: 5,
      labelText: (!this.options || this.showLabels ? 'B = ' : '') + `${BValue}`,
      labelValue: BValue,
      align: TextLabel.types.align.CENTER,
      verticalAlign: TextLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          panel: {
            dimensions: {
              b: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }, false, didChange);
      }
    });
    BLabel.text.position.set(
      NailPlate.DIMENSIONS_OFFSET + this.dimensions.d / 2,
      0,
      0
    );
    
    return {
      object: BLabel.object,
      text: BLabel.text
    };
  }

  createPlateDimensionC() {
    let top = {
      from: {
        x: 0,
        y: this.dimensions.a + this.horizontalPartThickness / 2,
        z: -this.dimensions.c / 2
      },
      to: {
        x: 0,
        y: this.dimensions.a + this.horizontalPartThickness / 2 + NailPlate.DIMENSIONS_OFFSET,
        z: -this.dimensions.c / 2,
      },
    };
    let bottom = {
      from: {
        x: 0,
        y: this.dimensions.a + this.horizontalPartThickness / 2,
        z: this.dimensions.c / 2
      },
      to: {
        x: 0,
        y: this.dimensions.a + this.horizontalPartThickness / 2 + NailPlate.DIMENSIONS_OFFSET,
        z: this.dimensions.c / 2
      },
    };

    const CValue = unitTransform(this.dimensions.c).from(defaultUnits.DISTANCE).to(Scene.units.distance);
    const CLabel = addDistanceLabel({
      top,
      bottom,
      size: this.dimensions.c,
      arrowDirection: Arrow.types.direction.Z,
      arrowSize: 5,
      labelText: (!this.options || this.showLabels ? 'C = ' : '') + `${CValue}`,
      labelValue: CValue,
      align: TextLabel.types.align.CENTER,
      verticalAlign: TextLabel.types.verticalAlign.BOTTOM,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          panel: {
            dimensions: {
              c: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }, false, didChange);
      }
    });
    CLabel.text.position.set(
      0,
      0,
      - this.horizontalPartThickness / 2 - this.dimensions.a - NailPlate.DIMENSIONS_OFFSET 
    );
    
    return {
      object: CLabel.object,
      text: CLabel.text
    };
  }

  create2DPlateDimensionD() {
    let top = {
      from: {
        x: this.verticalPartThickness / 2,
        y: -this.horizontalPartThickness / 2,
        z: -this.dimensions.d / 2
      },
      to: {
        x: this.verticalPartThickness / 2,
        y: -this.horizontalPartThickness / 2 - NailPlate.DIMENSIONS_OFFSET,
        z: -this.dimensions.d / 2,
      },
    };
    let bottom = {
      from: {
        x: this.verticalPartThickness / 2,
        y: -this.horizontalPartThickness / 2,
        z: this.dimensions.d / 2
      },
      to: {
        x: this.verticalPartThickness / 2,
        y: -this.horizontalPartThickness / 2 - NailPlate.DIMENSIONS_OFFSET,
        z: this.dimensions.d / 2
      },
    };

    const DValue = unitTransform(this.dimensions.d).from(defaultUnits.DISTANCE).to(Scene.units.distance);
    const DLabel = addDistanceLabel({
      top,
      bottom,
      size: this.dimensions.d,
      arrowDirection: Arrow.types.direction.Z,
      arrowSize: 5,
      labelText: `D = ${DValue}`,
      labelValue: DValue,
      align: TextLabel.types.align.CENTER,
      verticalAlign: TextLabel.types.verticalAlign.TOP,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          panel: {
            dimensions: {
              d: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }, false, didChange);
      }
    });
    DLabel.text.position.set(
      0,
      0,
      this.horizontalPartThickness / 2 + NailPlate.DIMENSIONS_OFFSET 
    );
    
    return {
      object: DLabel.object,
      text: DLabel.text
    };
  }

  create3DPlateDimensionD() {
    let top = {
      from: {
        x: this.dimensions.b - this.verticalPartThickness / 2,
        y: 0,
        z: -this.dimensions.d / 2
      },
      to: {
        x: this.dimensions.b - this.verticalPartThickness / 2 + NailPlate.DIMENSIONS_OFFSET,
        y: 0,
        z: -this.dimensions.d / 2,
      },
    };
    let bottom = {
      from: {
        x: this.dimensions.b - this.verticalPartThickness / 2,
        y: 0,
        z: this.dimensions.d / 2
      },
      to: {
        x: this.dimensions.b - this.verticalPartThickness / 2 + NailPlate.DIMENSIONS_OFFSET,
        y: 0,
        z: this.dimensions.d / 2
      },
    };

    const DValue = unitTransform(this.dimensions.d).from(defaultUnits.DISTANCE).to(Scene.units.distance);
    const DLabel = addDistanceLabel({
      top,
      bottom,
      size: this.dimensions.d,
      arrowDirection: Arrow.types.direction.Z,
      arrowSize: 5,
      labelText: (!this.options || this.showLabels ? 'D = ' : '') + `${DValue}`,
      labelValue: DValue,
      align: TextLabel.types.align.CENTER,
      verticalAlign: TextLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          panel: {
            dimensions: {
              d: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }, false, didChange);
      }
    });
    DLabel.text.position.set(
      0,
      0,
      NailPlate.DIMENSIONS_OFFSET 
    );
    
    return {
      object: DLabel.object,
      text: DLabel.text
    };
  }

} 



// WEBPACK FOOTER //
// ./src/object-view/objects/nail/nail-plate.js